/**
 * ONE STATUS VOCABULARY FOR THE WHOLE WORKSPACE.
 *
 * Every screen says where a thing stands with these words and these colours, so
 * "Needs attention" on the scene list and on the Overview are the same fact
 * drawn the same way. The words are never interchangeable: a scene that was
 * detected is READY_FOR_REVIEW, never CONFIRMED; a run that has not been asked
 * for is NOT_STARTED, never a zero.
 *
 * Colour repeats the label and never replaces it (`.ui-pill[data-state]`).
 */
export type WorkState =
  | 'NOT_STARTED'
  | 'PROCESSING'
  | 'NEEDS_ATTENTION'
  | 'READY_FOR_REVIEW'
  | 'IN_PROGRESS'
  | 'CONFIRMED'
  | 'COMPLETED'
  | 'OUTDATED'
  | 'FAILED'
  | 'EXCLUDED'
  | 'UNAVAILABLE';

export const WORK_STATE_LABELS: Readonly<Record<WorkState, string>> = Object.freeze({
  NOT_STARTED: 'Not started',
  PROCESSING: 'Processing',
  NEEDS_ATTENTION: 'Needs attention',
  READY_FOR_REVIEW: 'Ready for review',
  IN_PROGRESS: 'In progress',
  CONFIRMED: 'Confirmed',
  COMPLETED: 'Completed',
  OUTDATED: 'Out of date',
  FAILED: 'Failed',
  EXCLUDED: 'Excluded',
  UNAVAILABLE: 'Not available',
});

/**
 * A state in words. `label` overrides the vocabulary's word only where a screen
 * owns a more precise one for the SAME state (the server's "Outdated" on the
 * Overview), never to say a different state in this one's colour.
 */
export function StatusPill({
  state,
  label,
  testId,
  size = 'md',
}: {
  state: WorkState;
  label?: string;
  testId?: string;
  size?: 'sm' | 'md';
}) {
  return (
    <span className="ui-pill" data-state={state} data-size={size} data-testid={testId}>
      {label ?? WORK_STATE_LABELS[state]}
    </span>
  );
}

/**
 * A dot for a dense row, where a pill would crowd the heading out. The state
 * is still spoken: the dot carries it as its accessible name and its title.
 */
export function StatusDot({ state, label, testId }: { state: WorkState; label?: string; testId?: string }) {
  const text = label ?? WORK_STATE_LABELS[state];

  return <span className="ui-dot" data-state={state} role="img" aria-label={text} title={text} data-testid={testId} />;
}
